import { Store } from '@ngrx/store';
import { defer, distinctUntilChanged, finalize, map, MonoTypeOperatorFunction, Observable, timer } from 'rxjs';
import { LayoutActionType, selectLoading, State as LayoutState } from 'src/app/store/layout/layout.reducers';
import { BaseAction } from 'src/app/store/store.actions';

/** How long a draft listing may hold the FULL-SCREEN block before it is downgraded to the
 *  non-blocking top progress bar.
 *
 *  `listPendingDrafts` waits out this feature's whole draft-write chain before its GET goes out,
 *  and every write in that chain carries its own `SETTLE_LIST_TIMEOUT_MS` (10s) while the GET
 *  carries `LIST_DRAFTS_TIMEOUT_MS` (10s) on top. Those are PER-REQUEST bounds — deliberately, and
 *  the gate itself carries no clock, because an enqueue-anchored one latched healthy writes as
 *  failed for merely queueing (review round 15). So the wait the mounting screen can face is the
 *  SUM of the queued writes' deadlines: a mount carrying a queued settle plus two picker deletes
 *  faces ~40s against a proxy that accepts sockets and never answers.
 *
 *  Announcing that wait is right — silent, it reads as a broken Search button on a screen that
 *  looks idle — but `LayoutActionType.LoadingBlock` is a full-screen, undismissable `ngx-ui-loader`
 *  overlay, and 40s of it is worse than the silence it replaced. The cap applies to the OVERLAY
 *  ONLY: the gate keeps waiting exactly as long as it did, `hasPendingDrafts` still holds Search
 *  and autosave, and the progress bar left behind still says the app is busy. Nothing about which
 *  drafts are offered changes with this constant. */
export const DRAFT_LISTING_BLOCK_MAX_MS = 3000;

/** How many times the block may be re-raised after somebody else lowered it (see the re-raise
 *  guard below). A ceiling, not a budget: the guard only fires when the flag it is holding reads
 *  `false`, so on a healthy screen it never fires at all. It exists because both flags are plain
 *  booleans that any component may write, and a component that answered a `true` with a `false`
 *  would otherwise trade this bounded window for an unbounded dispatch war. Reaching the ceiling
 *  means the overlay stays down — the pre-fix behaviour — rather than the app locking up. */
export const DRAFT_LISTING_BLOCK_MAX_RERAISES = 20;

/** Raises the full-screen block for the piped listing, downgrades it to the non-blocking progress
 *  bar past `DRAFT_LISTING_BLOCK_MAX_MS`, and clears whatever is still up when the listing settles
 *  — on completion, on failure, or on the subscriber being torn down (`finalize` covers all three,
 *  which is what makes an unmount safe).
 *
 *  Place it FIRST in the pipe, closest to the listing: its `finalize` must see the raw error, not a
 *  `catchError` that has already swapped it for a fallback, or the block outlives a failed listing.
 *
 *  Each `false` is dispatched only for the flag this operator is still HOLDING. Both flags are plain
 *  booleans in the layout slice with no ref counting, so an unconditional clear would put out a
 *  block or a progress bar some other screen had raised. That cuts both ways: past the downgrade
 *  this operator no longer owns `LoadingBlock` — it lowered it itself at `downgrade()` — so a
 *  `finalize` that cleared it regardless would dismiss a block raised in the meantime by something
 *  else. The session-open button on the transfer/unload screen raises exactly that block and then
 *  navigates, so a listing settling a moment later used to tear the overlay off mid-navigation.
 *
 *  Owning only the bar past the downgrade is why the bar is raised and lowered with
 *  `LayoutActionType.LoadingNormal`, never with `Loading`. The `Loading` reducer case rebuilds the
 *  slice as `{normal, block: initialState.loading.block}` — a `Loading` dispatch of ANY payload
 *  clears `block` as a side effect — so the downgraded `finalize`'s `Loading: false` reintroduced
 *  the very bug the branch above it exists to prevent, through the other flag: same session-open
 *  scenario, same overlay torn off mid-navigation, just reached by settling AFTER the downgrade
 *  rather than before it. `LoadingNormal` leaves `block` untouched.
 *
 *  Not raising a flag nobody else can lower, though — which is why the re-raise guard below is
 *  needed and is not belt-and-braces. `LayoutActionType.LoadingBlock` is a plain boolean and the
 *  screens this operator runs on are the ones whose lists are loading at the same time:
 *  `DefaultListComponent`'s search subscriber dispatches `LoadingBlock: false` on EVERY emission
 *  (`default-list.component.ts`), as does `TableEditRowsComponent.formatValue`. Worse, the layout
 *  reducer's `Loading` case rebuilds the slice as `{normal, block: initialState.loading.block}` —
 *  i.e. a `Loading` dispatch of any payload clears `block` as a side effect — so the very same
 *  subscriber lowers the overlay twice over. A list emitting inside the window therefore took the
 *  overlay down while `hasPendingDrafts` still held Search and autosave dead: the silent
 *  broken-Search screen this operator exists to announce, restored a second later by the thing it
 *  was announcing.
 *
 *  So while it is still HOLDING the block (before the downgrade, at most `maxMs`), the guard
 *  watches the flag and puts it back up whenever it reads `false`, up to
 *  `DRAFT_LISTING_BLOCK_MAX_RERAISES` times. Deliberately scoped to that window and to that flag —
 *  and it stays scoped that way now that `LoadingNormal` exists. The original objection was
 *  mechanical (re-raising the bar meant dispatching `Loading`, which would knock down someone
 *  else's `block`) and `LoadingNormal` removes it; what remains is a judgement, which is that a bar
 *  another screen lowered is cosmetic — `hasPendingDrafts` still holds Search and autosave either
 *  way — and is not worth a second dispatch war for. The re-dispatch goes out on a `queueMicrotask`
 *  because the guard runs inside a store emission, and a reducer re-entered from a subscriber of
 *  the state it is producing is the shape to avoid.
 *
 *  Reaching `DRAFT_LISTING_BLOCK_MAX_RERAISES` ENDS the hold; it does not merely stop the
 *  re-raising. Past the ceiling this operator has watched the flag be lowered twenty times and
 *  declined to answer, so it is no longer what is holding the overlay up — and a `finalize` that
 *  still cleared it would take down a block raised in the meantime by something else, the exact
 *  defect the downgrade branch above exists to avoid, reached by the other route. `holdsBlock`
 *  tracks it: raised with the flag, dropped at the downgrade and at the ceiling, taken back up
 *  only by a re-raise this operator actually dispatched. Same watch for the bar past the downgrade
 *  (`holdsNormal`), so a bar another screen has since lowered and re-raised is not cleared by this
 *  `finalize` either.
 *
 *  What neither watch can see is a SHARED raise: both flags are plain booleans with no ref
 *  counting, so a list dispatching `Loading: true` while the bar is already up produces no
 *  transition to observe, and this `finalize` still lowers a bar that list is also relying on.
 *  Accepted, and bounded — the bar is the non-blocking one, `DefaultListComponent` re-dispatches
 *  its own loading state on every emission, and the alternative (never clearing what we might be
 *  sharing) strands a progress bar up for the life of the page with nothing left to lower it. */
export function blockWhileListingDrafts<T>(
  store: Store,
  maxMs: number = DRAFT_LISTING_BLOCK_MAX_MS,
): MonoTypeOperatorFunction<T> {
  return (source: Observable<T>) => defer(() => {
    let downgraded = false;
    let released = false;
    let reRaises = 0;
    let reRaiseQueued = false;
    /** Whether the full-screen block up right now is the one THIS operator put there. */
    let holdsBlock = false;
    /** Whether the progress bar up right now is the one this operator raised at the downgrade. */
    let holdsNormal = false;
    const downgrade = () => {
      if (downgraded) { return; }
      downgraded = true;
      // Only lower what this operator is still HOLDING. The ceiling branch in the guard below
      // drops the hold without lowering anything, so by the time the cap fires the overlay up may
      // be somebody else's — and the cap is unconditional. Clearing it regardless would tear off a
      // block raised in the meantime, the same defect `finalize` and the ceiling are written to
      // avoid, reached through the third exit. The bar is raised either way: this operator is
      // still waiting on the listing and the screen still has to say so.
      if (holdsBlock) {
        holdsBlock = false;
        store.dispatch(new BaseAction(LayoutActionType.LoadingBlock, false));
      }
      holdsNormal = true;
      store.dispatch(new BaseAction(LayoutActionType.LoadingNormal, true));
    };

    store.dispatch(new BaseAction(LayoutActionType.LoadingBlock, true));
    holdsBlock = true;
    const cap = timer(maxMs).subscribe(downgrade);

    const guard = (store as Store<LayoutState>).select(selectLoading).pipe(
      map((loading: { block?: boolean; normal?: boolean } | undefined) => ({
        blockUp: loading?.block === true,
        normalUp: loading?.normal === true,
      })),
      distinctUntilChanged((a, b) => a.blockUp === b.blockUp && a.normalUp === b.normalUp),
    ).subscribe(({ blockUp, normalUp }) => {
      // The bar this operator raised at the downgrade, lowered by somebody else: it is theirs to
      // manage now, and a bar another screen lowered is cosmetic (see the doc) — so the hold ends
      // here rather than turning into a second dispatch war.
      if (!normalUp) {
        holdsNormal = false;
      }
      if (blockUp || downgraded || reRaiseQueued) {
        return;
      }
      if (reRaises >= DRAFT_LISTING_BLOCK_MAX_RERAISES) {
        // Twenty answered lowerings is the ceiling: stop re-raising AND stop claiming the flag, or
        // the `finalize` below clears a block somebody else has since raised.
        holdsBlock = false;
        return;
      }
      reRaiseQueued = true;
      queueMicrotask(() => {
        reRaiseQueued = false;
        // Re-read the window inside the microtask: the cap may have fired, or the listing settled
        // and the `finalize` run, between the emission and here. Putting the block back up then
        // would leave a full-screen overlay nothing is left to take down.
        if (downgraded || released) { return; }
        reRaises++;
        store.dispatch(new BaseAction(LayoutActionType.LoadingBlock, true));
        holdsBlock = true;
      });
    });

    return source.pipe(finalize(() => {
      released = true;
      cap.unsubscribe();
      guard.unsubscribe();
      if (downgraded) {
        if (holdsNormal) {
          holdsNormal = false;
          store.dispatch(new BaseAction(LayoutActionType.LoadingNormal, false));
        }
      } else if (holdsBlock) {
        holdsBlock = false;
        store.dispatch(new BaseAction(LayoutActionType.LoadingBlock, false));
      }
    }));
  });
}
